import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Clock, Heart, Award, Star, ExternalLink, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../config/api";

// Fallback event configurations (used if API fetch fails)
const EVENTS_CONFIG = {
  "ramanavami-2026": {
    title: "Ramanavami 2026",
    date: "March 27, 2026",
    day: "FRIDAY, MARCH 27, 2026",
    time: "6:00 PM - 10:00 PM",
    location: "Chinmaya Mission (Chinmaya Hanuman)",
    address: "2156 Highbury Ave N, London, ON N5X 4A5",
    description: "Join us for the sacred celebration of Lord Rama's birth. Experience devotional bhajans, cultural programs, and community prayers.",
    sponsorshipSlug: "ramanavami"
  },
  "picnic-2026": {
    title: "Summer Picnic 2026",
    date: "July 11, 2026",
    day: "SATURDAY, JULY 11, 2026",
    time: "11:00 AM - 5:00 PM",
    location: "Douglas B. Weldon Park",
    address: "Richmond St, London ON N5X 4B2, Canada",
    maps_url: "https://www.google.com/maps/place/Weldon+Park,+Arva,+ON+N0M+1C0/@43.0471978,-81.2980047,2466m/data=!3m2!1e3!4b1!4m5!3m4!1s0x882eeee4bd78ed63:0x9862991fbd9bbd97!8m2!3d43.0467751!4d-81.2891408?hl=en&shorturl=1",
    description: "Annual family picnic with games, Kerala cuisine, and fun activities for all ages. A perfect day for families to connect.",
    sponsorshipSlug: "picnic"
  },
  "karkidaka-vavu-bali-2026": {
    title: "Karkidaka Vavu Bali 2026",
    date: "August 12, 2026",
    day: "WEDNESDAY, AUGUST 12, 2026",
    time: "6:00 PM - 9:00 PM",
    location: "Chinmaya Mission (Chinmaya Hanuman)",
    address: "2156 Highbury Ave N, London, ON N5X 4A5",
    description: "Sacred ritual to honor our ancestors. Join the community in performing traditional bali tharpanam.",
    sponsorshipSlug: "karkidaka"
  },
  "onam-2026": {
    title: "Onam 2026",
    date: "September 5, 2026",
    day: "SATURDAY, SEPTEMBER 5, 2026",
    time: "11:00 AM - 5:00 PM",
    location: "Sir Frederick Banting Secondary School",
    address: "25 Sherwood Forest Square, London, ON N6G 2C3",
    description: "Celebrate Kerala's harvest festival with traditional Onasadya, Thiruvathira, Pulikali, and cultural performances.",
    sponsorshipSlug: "onam"
  },
  "vidhyarambam-2026": {
    title: "Vidhyarambam 2026",
    date: "October 21, 2026",
    day: "WEDNESDAY, OCTOBER 21, 2026",
    time: "6:00 PM - 9:00 PM",
    location: "Chinmaya Mission (Chinmaya Hanuman)",
    address: "2156 Highbury Ave N, London, ON N5X 4A5",
    description: "Sacred initiation ceremony for children into the world of learning. Traditional rice writing ceremony on Vijayadashami.",
    sponsorshipSlug: "vidhyarambam"
  },
  "mandalakalam-2026": {
    title: "Mandalakalam 2026",
    date: "November 17, 2026",
    day: "TUESDAY, NOVEMBER 17, 2026",
    time: "6:00 PM - 9:00 PM",
    location: "Chinmaya Mission (Chinmaya Hanuman)",
    address: "2156 Highbury Ave N, London, ON N5X 4A5",
    description: "Observe the 41-day sacred period with special prayers and bhajans dedicated to Lord Ayyappa.",
    sponsorshipSlug: "mandalakalam"
  }
};

export default function GenericEventPage() {
  const { eventSlug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [megaSponsor, setMegaSponsor] = useState(null);
  
  // Get the slug from the URL path if not provided via params
  const pathSlug = eventSlug || window.location.pathname.split('/').pop();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // Try to fetch event from API by slug
        const response = await fetch(`${API}/events`);
        if (response.ok) {
          const events = await response.json();
          // Find event matching slug (check both slug field and pathSlug patterns)
          const dbEvent = events.find(e => 
            e.slug === pathSlug || 
            e.slug === pathSlug.replace('-2026', '') ||
            pathSlug.includes(e.slug)
          );
          
          if (dbEvent) {
            // Format the date for display
            const eventDate = new Date(dbEvent.date_time);
            const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
            const monthName = eventDate.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
            const day = eventDate.getDate();
            const year = eventDate.getFullYear();
            
            setEvent({
              title: dbEvent.title,
              date: eventDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
              day: `${dayName}, ${monthName} ${day}, ${year}`,
              time: dbEvent.time || "11:00 AM - 5:00 PM",
              location: dbEvent.venue || dbEvent.location || EVENTS_CONFIG[pathSlug]?.location,
              address: dbEvent.address || EVENTS_CONFIG[pathSlug]?.address,
              maps_url: dbEvent.maps_url || EVENTS_CONFIG[pathSlug]?.maps_url,
              description: dbEvent.description || EVENTS_CONFIG[pathSlug]?.description,
              sponsorshipSlug: EVENTS_CONFIG[pathSlug]?.sponsorshipSlug || pathSlug.replace('-2026', ''),
              hero_image: dbEvent.hero_image
            });
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.log("Using fallback config for event");
      }
      
      // Fall back to hardcoded config
      const fallbackEvent = EVENTS_CONFIG[pathSlug];
      setEvent(fallbackEvent);
      setLoading(false);
    };
    
    fetchEvent();
    
    // Fetch Mega sponsor for this event
    const fetchMegaSponsor = async () => {
      try {
        const res = await fetch(`${API}/public/events/${pathSlug}/mega-sponsor`);
        if (res.ok) {
          const data = await res.json();
          if (data.exists) {
            setMegaSponsor(data);
          }
        }
      } catch (error) {
        // Silently ignore
      }
    };
    fetchMegaSponsor();
  }, [pathSlug]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-kerala-cream flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="pt-20 min-h-screen bg-kerala-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-kerala-green mb-4">Event Coming Soon</h1>
          <p className="text-gray-600 mb-6">Details for this event will be available shortly.</p>
          <Link to="/">
            <Button className="btn-primary">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-kerala-cream" data-testid="generic-event-page">
      {/* Hero */}
      <section className="py-16 bg-kerala-green">
        <div className="section-container">
          <p className="text-kerala-gold text-sm tracking-widest uppercase mb-2">{event.day}</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-4">
            {event.title}
          </h1>
          <div className="flex flex-wrap gap-6 text-white/80 text-sm mt-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{event.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{event.time}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{event.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="section-container max-w-4xl">
          {/* Mega Sponsor Card - Only show if exists */}
          {megaSponsor && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-kerala-gold rounded-lg p-6 shadow-lg mb-8" data-testid="mega-sponsor-card">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-6 h-6 text-kerala-gold fill-kerala-gold" />
                <h3 className="font-heading text-xl text-kerala-green">Mega Sponsor</h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {megaSponsor.flyer_url && (
                  <a href={megaSponsor.flyer_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <img 
                      src={megaSponsor.flyer_url} 
                      alt={megaSponsor.business_name || "Mega Sponsor"} 
                      className="w-32 h-32 object-contain rounded-lg border border-kerala-gold/30 bg-white p-2 hover:shadow-lg transition-shadow"
                    />
                  </a>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-lg text-kerala-green mb-2">
                    {megaSponsor.business_name || megaSponsor.name}
                  </h4>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {megaSponsor.website && (
                      <a 
                        href={megaSponsor.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-kerala-green hover:text-kerala-gold transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Visit Website</span>
                      </a>
                    )}
                    {megaSponsor.phone && (
                      <a 
                        href={`tel:${megaSponsor.phone}`}
                        className="flex items-center gap-1 text-kerala-green hover:text-kerala-gold transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{megaSponsor.phone}</span>
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Thank you for supporting our community event!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Event Description */}
          <div className="bg-white border border-kerala-gold/20 rounded-lg p-8 shadow-lg mb-8">
            <h2 className="font-heading text-2xl text-kerala-green mb-4">About This Event</h2>
            <p className="text-gray-600 leading-relaxed">{event.description}</p>
          </div>

          {/* Event Details */}
          <div className="bg-white border border-kerala-gold/20 rounded-lg p-8 shadow-lg mb-8">
            <h2 className="font-heading text-2xl text-kerala-green mb-6">Event Details</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-kerala-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-kerala-green text-lg">{event.location}</p>
                  <p className="text-gray-500">{event.address}</p>
                  {event.maps_url && (
                    <a 
                      href={event.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-kerala-green hover:text-kerala-gold text-sm mt-2 inline-flex items-center gap-1"
                    >
                      View on Google Maps →
                    </a>
                  )}
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-kerala-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-kerala-green text-lg">Event Timings</p>
                  <p className="text-gray-500">{event.time}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Support This Event */}
          <div className="bg-kerala-cream border border-kerala-gold/20 rounded-lg p-8">
            <h2 className="font-heading text-2xl text-kerala-green mb-4">Support This Event</h2>
            <p className="text-gray-600 mb-6">
              Support this event through sponsorship or donation. Your contribution helps us create memorable cultural experiences for our community.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`/sponsorship/${event.sponsorshipSlug}`} className="flex-1">
                <Button className="w-full h-12 bg-kerala-green hover:bg-kerala-green/90 text-white">
                  <Award className="w-5 h-5 mr-2" />
                  Become a Sponsor
                </Button>
              </Link>
              <Link to="/donate" className="flex-1">
                <Button variant="outline" className="w-full h-12 border-kerala-green text-kerala-green hover:bg-kerala-green/5">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
